import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, TextField, Button, Stack, Grid, MenuItem, Switch, FormControlLabel, Slider,
  Alert, Chip, CircularProgress, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, Accordion,
  AccordionSummary, AccordionDetails, Checkbox, FormGroup, Tabs, Tab, Paper, Menu, Divider, Tooltip,
} from '@mui/material';
import {
  AutoAwesome, UploadFile, Save, CheckCircle, ExpandMore, Storefront, Psychology, Insights, AutoFixHigh,
  PlaylistAdd, Science, RadioButtonUnchecked, WarningAmber, MenuBook, Add,
} from '@mui/icons-material';
import { api } from '../config/api';
import InfoTip from '../components/InfoTip';
import { PLANTILLAS, TIPOS_PYME, ESTRUCTURA_BASE, aplicarDatos, PlantillaIndustria } from '../content/plantillasAgente';

const MODELOS = [
  { id: 'us.anthropic.claude-haiku-4-5-20251001-v1:0', label: 'Claude Haiku 4.5 (rápido, recomendado)' },
  { id: 'amazon.nova-lite-v1:0', label: 'Amazon Nova Lite (económico)' },
  { id: 'amazon.nova-pro-v1:0', label: 'Amazon Nova Pro (más potente)' },
];
const TONOS = [
  { id: 'profesional', desc: 'Claro y correcto, sin exceso de confianza.' },
  { id: 'cercano', desc: 'Como un buen vendedor de barrio: amable y directo.' },
  { id: 'amigable', desc: 'Alegre, con emojis y tuteo.' },
  { id: 'formal', desc: 'Trata de "usted". Para servicios profesionales o B2B.' },
  { id: 'vendedor', desc: 'Proactivo: sugiere productos y busca cerrar.' },
];
const tieneCompletar = (s?: string | null) => /\[COMPLETAR/i.test(s || '');

const Agente: React.FC = () => {
  const navigate = useNavigate();
  const [agente, setAgente] = useState<any>(null);
  const [original, setOriginal] = useState<string>('');
  const [perfil, setPerfil] = useState<any>(null);
  const [limites, setLimites] = useState({ kbMaxPrompt: 15000, kbMaxGuardado: 30000 });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<{ sev: any; txt: string } | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const modoArchivo = useRef<'reemplazar' | 'agregar'>('agregar');
  const [menuArchivo, setMenuArchivo] = useState<null | HTMLElement>(null);
  const [subiendo, setSubiendo] = useState(false);

  // Plantillas
  const [plOpen, setPlOpen] = useState(false);
  const [plSel, setPlSel] = useState<PlantillaIndustria | null>(null);
  const [pymeSel, setPymeSel] = useState<string>('');
  const [plTab, setPlTab] = useState(0);
  const [aplicar, setAplicar] = useState({ prompt: true, base: true, bienvenida: true, identidad: true });

  // IA
  const [mejOpen, setMejOpen] = useState(false);
  const [objetivo, setObjetivo] = useState('');
  const [mejorando, setMejorando] = useState(false);
  const [mejora, setMejora] = useState<any>(null);
  const [analizando, setAnalizando] = useState(false);
  const [analisis, setAnalisis] = useState<any>(null);
  const [ordenando, setOrdenando] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/agente'),
      api.get('/perfil').catch(() => ({ data: {} })),
      api.get('/agente/limites').catch(() => ({ data: null })),
    ]).then(([a, p, l]: any[]) => {
      setAgente(a.data.agente);
      setOriginal(JSON.stringify(a.data.agente));
      setPerfil(p.data?.empresa || null);
      if (l.data) setLimites(l.data);
      if (p.data?.empresa?.tipo_pyme) setPymeSel(p.data.empresa.tipo_pyme);
    }).finally(() => setLoading(false));
  }, []);

  const sucio = agente && JSON.stringify(agente) !== original;
  const set = (k: string) => (e: any) => setAgente({ ...agente, [k]: e.target.value });
  const aviso = (txt: string, sev: any = 'success') => setMsg({ sev, txt });

  const datosPerfil = useMemo(() => ({
    empresa: perfil?.nombre_fantasia, agente: agente?.nombre,
    direccion: [perfil?.direccion, perfil?.comuna, perfil?.ciudad].filter(Boolean).join(', '),
    horario: perfil?.horario_texto, pagos: perfil?.medios_pago, despacho: perfil?.zonas_despacho,
  }), [perfil, agente?.nombre]);

  const guardar = async () => {
    setSaving(true); setMsg(null);
    try {
      const { data } = await api.put('/agente', agente);
      setAgente(data.agente); setOriginal(JSON.stringify(data.agente));
      aviso(tieneCompletar(data.agente.system_prompt) || tieneCompletar(data.agente.base_conocimiento)
        ? 'Guardado. Ojo: aún quedan textos [COMPLETAR] por reemplazar.' : 'Agente guardado correctamente');
    } catch { aviso('Error al guardar', 'error'); }
    finally { setSaving(false); }
  };

  const toggleActivo = async (activo: boolean) => {
    try {
      await api.post('/agente/toggle', { activo });
      setAgente({ ...agente, activo });
      setOriginal(JSON.stringify({ ...JSON.parse(original), activo }));
    } catch (err: any) { aviso(err.response?.data?.error || 'No se pudo cambiar el estado del agente', 'error'); }
  };

  const subirArchivo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const fd = new FormData();
    fd.append('archivo', file);
    fd.append('modo', modoArchivo.current);
    setSubiendo(true);
    try {
      const { data } = await api.post('/agente/base-conocimiento', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const nuevo = { ...agente, base_conocimiento: data.base };
      setAgente(nuevo); setOriginal(JSON.stringify(nuevo));
      setAnalisis(null);
      aviso(`“${file.name}” ${modoArchivo.current === 'agregar' ? 'agregado' : 'cargado'}: la base tiene ${data.caracteres.toLocaleString('es-CL')} caracteres.${data.recortado ? ' Se recortó al máximo permitido.' : ''}`);
    } catch (err: any) { aviso(err.response?.data?.error || 'Error procesando el archivo', 'error'); }
    finally { setSubiendo(false); }
  };

  // ─── Plantillas ───
  const abrirPlantillas = () => {
    const sug = PLANTILLAS.find((p) => p.id === perfil?.industria) || null;
    setPlSel(sug); setPlTab(0); setPlOpen(true);
  };
  const pyme = TIPOS_PYME.find((t) => t.id === pymeSel);
  const previa = plSel ? {
    nombre: plSel.nombreAgente,
    prompt: aplicarDatos(plSel.prompt + (pyme ? `\n\n${pyme.prompt}` : ''), { ...datosPerfil, agente: plSel.nombreAgente }),
    base: aplicarDatos(plSel.base, datosPerfil),
    bienvenida: aplicarDatos(plSel.bienvenida, datosPerfil),
  } : null;
  const usarPlantilla = () => {
    if (!plSel || !previa) return;
    const reemplaza = (aplicar.prompt && agente.system_prompt) || (aplicar.base && agente.base_conocimiento);
    if (reemplaza && !window.confirm('La plantilla reemplazará el texto actual de los campos marcados. ¿Continuar?')) return;
    setAgente({
      ...agente,
      ...(aplicar.identidad ? { nombre: previa.nombre, tono: plSel.tono } : {}),
      ...(aplicar.prompt ? { system_prompt: previa.prompt } : {}),
      ...(aplicar.base ? { base_conocimiento: previa.base } : {}),
      ...(aplicar.bienvenida ? { mensaje_bienvenida: previa.bienvenida } : {}),
    });
    setPlOpen(false); setAnalisis(null);
    aviso(`Plantilla “${plSel.nombre}” cargada. Reemplaza los [COMPLETAR] con tus datos y pulsa Guardar.`, 'info');
  };

  // ─── IA ───
  const mejorarPrompt = async () => {
    setMejorando(true); setMejora(null);
    try {
      const { data } = await api.post('/agente/mejorar-prompt', { system_prompt: agente.system_prompt, objetivo, nombre: agente.nombre, tono: agente.tono });
      setMejora(data);
    } catch (e: any) { aviso(e.response?.data?.error || 'No se pudo mejorar el prompt', 'error'); setMejOpen(false); }
    finally { setMejorando(false); }
  };
  const analizarBase = async () => {
    setAnalizando(true); setAnalisis(null);
    try {
      const { data } = await api.post('/agente/analizar-base', { base_conocimiento: agente.base_conocimiento });
      setAnalisis(data);
    } catch (e: any) { aviso(e.response?.data?.error || 'No se pudo analizar la base', 'error'); }
    finally { setAnalizando(false); }
  };
  const ordenarBase = async () => {
    if (!window.confirm('La IA reorganizará el texto en secciones sin inventar datos. Podrás revisarlo antes de guardar. ¿Continuar?')) return;
    setOrdenando(true);
    try {
      const { data } = await api.post('/agente/ordenar-base', { texto: agente.base_conocimiento });
      setAgente({ ...agente, base_conocimiento: data.base });
      aviso('Base reorganizada. Revísala y pulsa Guardar para conservar los cambios.', 'info');
    } catch (e: any) { aviso(e.response?.data?.error || 'No se pudo ordenar la base', 'error'); }
    finally { setOrdenando(false); }
  };
  const agregarABase = (texto: string) => setAgente({ ...agente, base_conocimiento: `${(agente.base_conocimiento || '').trim()}\n\n${texto}`.trim() });

  if (loading) return <Box sx={{ textAlign: 'center', mt: 6 }}><CircularProgress /></Box>;
  if (!agente) return <Alert severity="error">No se pudo cargar el agente.</Alert>;

  const kbLen = (agente.base_conocimiento || '').length;
  const kbPct = Math.min(100, Math.round((kbLen / limites.kbMaxPrompt) * 100));
  const perfilCompleto = !!(perfil?.nombre_fantasia && perfil?.horario_texto && (perfil?.direccion || perfil?.zonas_despacho));
  const checklist = [
    { ok: !!agente.nombre && agente.nombre !== 'Mi Agente', label: 'Nombre del agente' },
    { ok: (agente.system_prompt || '').length > 150 && !tieneCompletar(agente.system_prompt), label: 'Instrucciones completas' },
    { ok: kbLen > 300 && !tieneCompletar(agente.base_conocimiento), label: 'Base de conocimiento' },
    { ok: perfilCompleto, label: 'Datos de la empresa', path: '/app/perfil' },
    { ok: !!agente.activo, label: 'Agente activo' },
  ];
  const listo = Math.round((checklist.filter((c) => c.ok).length / checklist.length) * 100);

  return (
    <Box sx={{ pb: 8 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <AutoAwesome color="primary" />
            <Typography variant="h4">Mi Agente</Typography>
          </Stack>
          <Typography variant="body1" color="text.secondary">Dale personalidad, reglas y conocimiento a tu agente. Así atenderá como tu mejor vendedor.</Typography>
        </Box>
        <Button variant="contained" color="secondary" size="large" startIcon={<Storefront />} onClick={abrirPlantillas}
          sx={{ bgcolor: '#075E54', '&:hover': { bgcolor: '#128C7E' }, whiteSpace: 'nowrap' }}>
          Plantillas por industria
        </Button>
      </Stack>

      {msg && <Alert severity={msg.sev} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.txt}</Alert>}

      {/* Checklist de preparación */}
      <Card sx={{ mb: 2 }}>
        <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <Box sx={{ minWidth: 190 }}>
              <Typography variant="subtitle2">Tu agente está {listo}% listo</Typography>
              <LinearProgress variant="determinate" value={listo} color={listo === 100 ? 'success' : 'primary'} sx={{ height: 8, borderRadius: 4, mt: 0.5 }} />
            </Box>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {checklist.map((c) => (
                <Chip key={c.label} size="small" label={c.label}
                  icon={c.ok ? <CheckCircle /> : <RadioButtonUnchecked />}
                  color={c.ok ? 'success' : 'default'} variant={c.ok ? 'filled' : 'outlined'}
                  onClick={c.path ? () => navigate(c.path!) : undefined} />
              ))}
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          {/* Identidad */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Identidad <InfoTip texto="Cómo se presenta el agente ante tus clientes." />
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Nombre del agente" fullWidth value={agente.nombre || ''} onChange={set('nombre')}
                    helperText="Un nombre humano genera más confianza" />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Descripción (solo para ti)" fullWidth value={agente.descripcion || ''} onChange={set('descripcion')} helperText="No la ve el cliente" />
                </Grid>
                <Grid item xs={12}>
                  <TextField label="Mensaje de bienvenida" fullWidth value={agente.mensaje_bienvenida || ''} onChange={set('mensaje_bienvenida')}
                    helperText="Se usa si la IA no puede responder. Ej: ¡Hola! Soy Sofía de Dulce Hogar 🎂 ¿En qué te ayudo?" />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Instrucciones */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                <Typography variant="h6">
                  <Psychology sx={{ verticalAlign: 'middle', mr: 0.5, color: 'primary.main' }} />
                  Instrucciones (prompt)
                  <InfoTip texto="Le dicen al agente CÓMO comportarse: su rol, su objetivo, qué hacer y qué evitar. Los datos del negocio van en la base de conocimiento."
                    ejemplo={'QUÉ NO HACER\n- No confirmes pedidos para el mismo día.\n- No des descuentos sin autorización.'} />
                </Typography>
                <Button variant="outlined" startIcon={<AutoFixHigh />} onClick={() => { setMejora(null); setMejOpen(true); }}>Mejorar con IA</Button>
              </Stack>
              <TextField
                fullWidth multiline minRows={8} maxRows={22}
                placeholder={'ROL\nEres Sofía, asesora de “Pastelería Dulce Hogar”.\n\nOBJETIVO\nTomar pedidos de tortas con 48 h de anticipación.\n\nQUÉ NO HACER\n- No inventes precios.'}
                value={agente.system_prompt || ''} onChange={set('system_prompt')}
              />
              <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                <Typography variant="caption" color={tieneCompletar(agente.system_prompt) ? 'warning.main' : 'text.secondary'}>
                  {tieneCompletar(agente.system_prompt) ? '⚠️ Hay textos [COMPLETAR] por reemplazar' : 'Tip: usa secciones ROL, OBJETIVO, QUÉ HACER, QUÉ NO HACER, CUÁNDO DERIVAR.'}
                </Typography>
                <Typography variant="caption" color="text.secondary">{(agente.system_prompt || '').length.toLocaleString('es-CL')} caracteres</Typography>
              </Stack>
            </CardContent>
          </Card>

          {/* Base de conocimiento */}
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                <Typography variant="h6">
                  <MenuBook sx={{ verticalAlign: 'middle', mr: 0.5, color: 'primary.main' }} />
                  Base de conocimiento
                  <InfoTip texto="Es la “memoria” del agente: todo lo que debe SABER de tu negocio. Responde solo con esto; si un dato no está aquí, no lo inventa." />
                </Typography>
                {analisis && <Chip color={analisis.puntaje >= 75 ? 'success' : analisis.puntaje >= 50 ? 'warning' : 'error'} label={`Calidad: ${analisis.puntaje}/100`} />}
              </Stack>

              <Accordion disableGutters elevation={0} sx={{ border: '1px solid #E2E8F0', borderRadius: 2, mb: 2, '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2">📘 Cómo armar una base que haga brillar a tu agente</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Qué incluir</Typography>
                      <Typography variant="body2" component="div">
                        <ol style={{ margin: 0, paddingLeft: 18 }}>
                          <li>Productos o servicios con precio y lo que incluyen.</li>
                          <li>Horarios, ubicación, despacho o cobertura.</li>
                          <li>Medios de pago, plazos y condiciones.</li>
                          <li>Políticas: cambios, devoluciones, garantías, cancelaciones.</li>
                          <li>Las 10 preguntas que más te hacen, con su respuesta.</li>
                        </ol>
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>Cómo escribirla</Typography>
                      <Typography variant="body2" component="div">
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          <li>Títulos en MAYÚSCULAS por tema.</li>
                          <li>Una idea por línea: “Despacho: Santiago, $3.990, 24-48 h”.</li>
                          <li>Preguntas frecuentes como “P: … / R: …”.</li>
                          <li>Un solo precio por producto (sin versiones viejas).</li>
                          <li>Actualízala cuando cambien precios o stock.</li>
                        </ul>
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Paper variant="outlined" sx={{ p: 1.5, bgcolor: '#F8FAFC', fontFamily: 'monospace', fontSize: 13, whiteSpace: 'pre-line' }}>
                        {'PRODUCTOS Y PRECIOS\n- Torta tres leches (15 personas) – $24.990\n\nDESPACHO\n- Providencia y Ñuñoa: $3.000 · Resto de Santiago: $5.000\n\nPREGUNTAS FRECUENTES\nP: ¿Hacen tortas sin azúcar? / R: Sí, con 72 h de anticipación y +$3.000.'}
                      </Paper>
                      <Typography variant="caption" color="text.secondary">
                        Puedes subir varios PDF o TXT (menú, catálogo, políticas): elige “Agregar” para sumarlos. La IA lee hasta {limites.kbMaxPrompt.toLocaleString('es-CL')} caracteres en cada respuesta.
                      </Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>

              <input ref={fileRef} type="file" accept=".pdf,.txt,.md,.csv" hidden onChange={subirArchivo} />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
                <Button variant="outlined" startIcon={subiendo ? <CircularProgress size={16} /> : <UploadFile />} disabled={subiendo} onClick={(e) => setMenuArchivo(e.currentTarget)}>
                  Subir PDF / TXT
                </Button>
                <Menu anchorEl={menuArchivo} open={!!menuArchivo} onClose={() => setMenuArchivo(null)}>
                  <MenuItem onClick={() => { modoArchivo.current = 'agregar'; setMenuArchivo(null); fileRef.current?.click(); }}>
                    <Add fontSize="small" sx={{ mr: 1 }} /> Agregar al final de la base actual
                  </MenuItem>
                  <MenuItem onClick={() => { modoArchivo.current = 'reemplazar'; setMenuArchivo(null); fileRef.current?.click(); }}>
                    <UploadFile fontSize="small" sx={{ mr: 1 }} /> Reemplazar toda la base
                  </MenuItem>
                </Menu>
                <Tooltip title="Agrega una estructura con secciones y [COMPLETAR] para que solo llenes tus datos">
                  <Button variant="outlined" startIcon={<PlaylistAdd />} onClick={() => agregarABase(aplicarDatos(ESTRUCTURA_BASE, datosPerfil))}>Insertar estructura</Button>
                </Tooltip>
                <Tooltip title="La IA ordena tu texto en secciones claras, sin inventar datos">
                  <span><Button variant="outlined" startIcon={ordenando ? <CircularProgress size={16} /> : <AutoFixHigh />} onClick={ordenarBase} disabled={ordenando || kbLen < 30}>Ordenar con IA</Button></span>
                </Tooltip>
                <Tooltip title="Puntaje, qué le falta y preguntas para probar">
                  <span><Button variant="contained" startIcon={analizando ? <CircularProgress size={16} color="inherit" /> : <Insights />} onClick={analizarBase} disabled={analizando || kbLen < 30}>Analizar con IA</Button></span>
                </Tooltip>
              </Stack>

              <TextField
                fullWidth multiline minRows={10} maxRows={28}
                placeholder="Pega aquí tu información o sube un archivo…"
                value={agente.base_conocimiento || ''} onChange={set('base_conocimiento')}
                inputProps={{ maxLength: limites.kbMaxGuardado, style: { fontFamily: 'ui-monospace, Menlo, monospace', fontSize: 13 } }}
              />
              <Box sx={{ mt: 1 }}>
                <LinearProgress variant="determinate" value={kbPct} color={kbLen > limites.kbMaxPrompt ? 'error' : kbPct > 85 ? 'warning' : 'primary'} sx={{ height: 6, borderRadius: 3 }} />
                <Stack direction="row" justifyContent="space-between" sx={{ mt: 0.5 }}>
                  <Typography variant="caption" color={tieneCompletar(agente.base_conocimiento) ? 'warning.main' : 'text.secondary'}>
                    {kbLen > limites.kbMaxPrompt
                      ? `⚠️ La IA solo lee los primeros ${limites.kbMaxPrompt.toLocaleString('es-CL')} caracteres. Usa “Ordenar con IA” o deja lo más importante arriba.`
                      : tieneCompletar(agente.base_conocimiento) ? '⚠️ Hay textos [COMPLETAR] por reemplazar' : 'Lo más importante, arriba.'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">{kbLen.toLocaleString('es-CL')} / {limites.kbMaxPrompt.toLocaleString('es-CL')}</Typography>
                </Stack>
              </Box>

              {/* Resultado del análisis */}
              {analisis && (
                <Paper variant="outlined" sx={{ mt: 2, p: 2, borderRadius: 2, borderColor: 'primary.light' }}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
                    <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                      <CircularProgress variant="determinate" value={analisis.puntaje} size={56} thickness={5}
                        color={analisis.puntaje >= 75 ? 'success' : analisis.puntaje >= 50 ? 'warning' : 'error'} />
                      <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography variant="subtitle2">{analisis.puntaje}</Typography>
                      </Box>
                    </Box>
                    <Typography variant="body2">{analisis.resumen}</Typography>
                  </Stack>
                  {analisis.fortalezas?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="overline" color="success.main">Lo que está bien</Typography>
                      {analisis.fortalezas.map((f: string) => <Typography key={f} variant="body2">✓ {f}</Typography>)}
                    </Box>
                  )}
                  {analisis.faltantes?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="overline" color="warning.main">Lo que le falta</Typography>
                      <Stack spacing={1}>
                        {analisis.faltantes.map((f: any, i: number) => (
                          <Paper key={i} variant="outlined" sx={{ p: 1.2 }}>
                            <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1}>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 700 }}>{f.tema}</Typography>
                                <Typography variant="caption" color="text.secondary">{f.por_que}</Typography>
                                {f.ejemplo && <Typography variant="caption" component="pre" sx={{ display: 'block', whiteSpace: 'pre-wrap', mt: 0.5, fontFamily: 'monospace' }}>{f.ejemplo}</Typography>}
                              </Box>
                              {f.ejemplo && <Button size="small" startIcon={<Add />} onClick={() => agregarABase(f.ejemplo)} sx={{ whiteSpace: 'nowrap', alignSelf: 'flex-start' }}>Agregar</Button>}
                            </Stack>
                          </Paper>
                        ))}
                      </Stack>
                    </Box>
                  )}
                  {analisis.problemas?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="overline" color="error.main">Para corregir</Typography>
                      {analisis.problemas.map((p: string) => <Typography key={p} variant="body2"><WarningAmber sx={{ fontSize: 16, verticalAlign: 'text-bottom', color: 'error.main' }} /> {p}</Typography>)}
                    </Box>
                  )}
                  {analisis.preguntas_prueba?.length > 0 && (
                    <Box>
                      <Divider sx={{ my: 1 }} />
                      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" useFlexGap>
                        <Typography variant="overline">Pruébalo con estas preguntas</Typography>
                        <Button size="small" startIcon={<Science />} onClick={() => navigate(`/app/playground?q=${encodeURIComponent(analisis.preguntas_prueba.join('|'))}`)}>
                          Abrir Playground
                        </Button>
                      </Stack>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {analisis.preguntas_prueba.map((q: string) => <Chip key={q} size="small" label={q} variant="outlined" />)}
                      </Stack>
                      {sucio && <Typography variant="caption" color="warning.main">Guarda antes de probar para que el Playground use la última versión.</Typography>}
                    </Box>
                  )}
                </Paper>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 1 }}>Estado</Typography>
              <FormControlLabel
                control={<Switch checked={!!agente.activo} onChange={(e) => toggleActivo(e.target.checked)} color="success" />}
                label={agente.activo ? 'Agente activo' : 'Agente en pausa'}
              />
              <Typography variant="body2" color="text.secondary">
                {agente.activo ? 'Responde automáticamente en tus canales conectados.' : 'Los mensajes se guardan, pero nadie responde automáticamente.'}
              </Typography>
              {agente.activo && <Chip icon={<CheckCircle />} color="success" label="Respondiendo" size="small" sx={{ mt: 1 }} />}
              <Button fullWidth variant="outlined" startIcon={<Science />} sx={{ mt: 2 }} onClick={() => navigate('/app/playground')}>Probar en el Playground</Button>
            </CardContent>
          </Card>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Comportamiento</Typography>
              <TextField select label="Modelo de IA" fullWidth sx={{ mb: 2 }} value={agente.modelo || MODELOS[0].id} onChange={set('modelo')}
                helperText="Haiku 4.5 es el mejor equilibrio entre calidad y rapidez">
                {MODELOS.map((m) => <MenuItem key={m.id} value={m.id}>{m.label}</MenuItem>)}
              </TextField>
              <TextField select label="Tono" fullWidth sx={{ mb: 2 }} value={agente.tono || 'profesional'} onChange={set('tono')}
                helperText={TONOS.find((t) => t.id === (agente.tono || 'profesional'))?.desc}>
                {TONOS.map((t) => <MenuItem key={t.id} value={t.id}>{t.id}</MenuItem>)}
              </TextField>
              <Typography variant="body2" color="text.secondary">
                Creatividad: {Number(agente.temperatura).toFixed(1)}
                <InfoTip texto="Baja (0.2-0.4): respuestas más precisas y repetibles. Alta (0.7-1): más variadas. Para ventas y datos, recomendamos 0.4-0.6." />
              </Typography>
              <Slider min={0} max={1} step={0.1} value={Number(agente.temperatura) || 0.6}
                marks={[{ value: 0.2, label: 'Precisa' }, { value: 0.9, label: 'Creativa' }]}
                onChange={(_, v) => setAgente({ ...agente, temperatura: v })} />
              <FormControlLabel
                control={<Switch checked={!!agente.captura_leads} onChange={(e) => setAgente({ ...agente, captura_leads: e.target.checked })} />}
                label={<span>Capturar leads <InfoTip texto="Después de cada conversación, la IA califica el interés de compra (0-100). Desde 40 se crea un lead y una tarjeta en el Pipeline." /></span>}
              />
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>💡 Instrucciones vs. base de conocimiento</Typography>
              <Typography variant="body2" color="text.secondary">
                <b>Instrucciones</b>: cómo actúa (“pide el nombre antes de cotizar”).<br />
                <b>Base</b>: lo que sabe (“la torta mediana cuesta $18.990”).<br />
                <b>Mi perfil</b>: tus datos fijos (dirección, horario, pagos) se agregan solos.
              </Typography>
              <Button size="small" sx={{ mt: 1 }} onClick={() => navigate('/app/perfil')}>Completar mi perfil</Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Barra de guardado */}
      <Paper elevation={6} sx={{
        position: 'sticky', bottom: 16, mt: 3, p: 1.5, px: 2, borderRadius: 3, display: 'flex', alignItems: 'center',
        justifyContent: 'space-between', gap: 2, bgcolor: sucio ? '#FFF8E1' : '#fff',
      }}>
        <Typography variant="body2" color={sucio ? 'warning.dark' : 'text.secondary'}>
          {sucio ? 'Tienes cambios sin guardar' : 'Todo guardado'}
        </Typography>
        <Button variant="contained" size="large" startIcon={<Save />} onClick={guardar} disabled={saving || !sucio}>
          {saving ? 'Guardando...' : 'Guardar cambios'}
        </Button>
      </Paper>

      {/* Diálogo de plantillas */}
      <Dialog open={plOpen} onClose={() => setPlOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle>Plantillas por industria y tipo de pyme</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            1. Elige tu rubro · 2. Elige cómo vendes · 3. Revisa y usa. Los datos de “Mi perfil” se completan solos; lo demás queda como [COMPLETAR].
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <Typography variant="overline">1. Tu industria</Typography>
              <Grid container spacing={1}>
                {PLANTILLAS.map((p) => (
                  <Grid item xs={6} key={p.id}>
                    <Paper variant="outlined" onClick={() => { setPlSel(p); setPlTab(0); }}
                      sx={{ p: 1.2, cursor: 'pointer', height: '100%', borderWidth: 2, borderColor: plSel?.id === p.id ? 'primary.main' : 'divider', bgcolor: plSel?.id === p.id ? '#E8F8EE' : '#fff', transition: 'all .15s', '&:hover': { borderColor: 'primary.light' } }}>
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>{p.emoji} {p.nombre}</Typography>
                      <Typography variant="caption" color="text.secondary">{p.descripcion}</Typography>
                      {perfil?.industria === p.id && <Chip size="small" color="success" label="Tu rubro" sx={{ mt: 0.5 }} />}
                    </Paper>
                  </Grid>
                ))}
              </Grid>
              <Typography variant="overline" sx={{ display: 'block', mt: 2 }}>2. Cómo vendes</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label="Sin ajuste" variant={!pymeSel ? 'filled' : 'outlined'} color={!pymeSel ? 'primary' : 'default'} onClick={() => setPymeSel('')} />
                {TIPOS_PYME.map((t) => (
                  <Tooltip key={t.id} title={t.descripcion}>
                    <Chip label={`${t.emoji} ${t.nombre}`} variant={pymeSel === t.id ? 'filled' : 'outlined'} color={pymeSel === t.id ? 'primary' : 'default'} onClick={() => setPymeSel(t.id)} />
                  </Tooltip>
                ))}
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="overline">3. Vista previa</Typography>
              {!previa ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>Elige una industria para ver la plantilla</Paper>
              ) : (
                <>
                  <Tabs value={plTab} onChange={(_, v) => setPlTab(v)} sx={{ minHeight: 36 }}>
                    <Tab label="Instrucciones" sx={{ minHeight: 36 }} /><Tab label="Base de conocimiento" sx={{ minHeight: 36 }} /><Tab label="Preguntas para probar" sx={{ minHeight: 36 }} />
                  </Tabs>
                  <Paper variant="outlined" sx={{ p: 1.5, mt: 1, maxHeight: 360, overflowY: 'auto', bgcolor: '#F8FAFC' }}>
                    {plTab === 0 && <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}>{`Bienvenida: ${previa.bienvenida}\n\n${previa.prompt}`}</Typography>}
                    {plTab === 1 && <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 13, m: 0 }}>{previa.base}</Typography>}
                    {plTab === 2 && plSel?.preguntasPrueba.map((q) => <Typography key={q} variant="body2">💬 {q}</Typography>)}
                  </Paper>
                  <FormGroup row sx={{ mt: 1 }}>
                    <FormControlLabel control={<Checkbox checked={aplicar.identidad} onChange={(e) => setAplicar({ ...aplicar, identidad: e.target.checked })} />} label={`Nombre y tono (${plSel?.nombreAgente}, ${plSel?.tono})`} />
                    <FormControlLabel control={<Checkbox checked={aplicar.prompt} onChange={(e) => setAplicar({ ...aplicar, prompt: e.target.checked })} />} label="Instrucciones" />
                    <FormControlLabel control={<Checkbox checked={aplicar.base} onChange={(e) => setAplicar({ ...aplicar, base: e.target.checked })} />} label="Base de conocimiento" />
                    <FormControlLabel control={<Checkbox checked={aplicar.bienvenida} onChange={(e) => setAplicar({ ...aplicar, bienvenida: e.target.checked })} />} label="Bienvenida" />
                  </FormGroup>
                </>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPlOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={usarPlantilla} disabled={!plSel || !Object.values(aplicar).some(Boolean)}>Usar plantilla</Button>
        </DialogActions>
      </Dialog>

      {/* Diálogo: mejorar prompt con IA */}
      <Dialog open={mejOpen} onClose={() => !mejorando && setMejOpen(false)} fullWidth maxWidth={mejora ? 'lg' : 'sm'}>
        <DialogTitle><AutoFixHigh sx={{ verticalAlign: 'middle', mr: 1 }} />Mejorar instrucciones con IA</DialogTitle>
        <DialogContent dividers>
          {!mejora ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                La IA reordena tus instrucciones por secciones, agrega buenas prácticas de atención por WhatsApp y te explica cada cambio. No inventa datos: lo que falte lo marca como [COMPLETAR].
              </Typography>
              <TextField label="¿Qué quieres lograr? (opcional)" value={objetivo} onChange={(e) => setObjetivo(e.target.value)}
                placeholder="Ej: que agende más visitas · que venda el plan anual · menos derivaciones a humano" />
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {['Vender más', 'Agendar más citas', 'Respuestas más cortas', 'Más cercano', 'Calificar mejor a los interesados'].map((o) => (
                  <Chip key={o} label={o} size="small" variant="outlined" onClick={() => setObjetivo(o)} />
                ))}
              </Stack>
              {mejorando && <LinearProgress />}
            </Stack>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="overline">Actual</Typography>
                <Paper variant="outlined" sx={{ p: 1.5, maxHeight: 380, overflowY: 'auto', bgcolor: '#F8FAFC' }}>
                  <Typography variant="body2" component="pre" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', m: 0 }}>{agente.system_prompt || '(vacío)'}</Typography>
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="overline" color="primary">Propuesta (puedes editarla)</Typography>
                <TextField fullWidth multiline minRows={14} maxRows={18} value={mejora.prompt_mejorado}
                  onChange={(e) => setMejora({ ...mejora, prompt_mejorado: e.target.value })} />
              </Grid>
              {mejora.cambios?.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="overline">Qué cambió y por qué</Typography>
                  {mejora.cambios.map((c: string) => <Typography key={c} variant="body2">✓ {c}</Typography>)}
                </Grid>
              )}
              {mejora.datos_faltantes?.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Typography variant="overline" color="warning.main">Datos que conviene agregar</Typography>
                  {mejora.datos_faltantes.map((c: string) => <Typography key={c} variant="body2">• {c}</Typography>)}
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMejOpen(false)} disabled={mejorando}>Cancelar</Button>
          {!mejora ? (
            <Button variant="contained" onClick={mejorarPrompt} disabled={mejorando} startIcon={mejorando ? <CircularProgress size={16} color="inherit" /> : <AutoFixHigh />}>
              {mejorando ? 'Pensando…' : 'Generar propuesta'}
            </Button>
          ) : (
            <>
              <Button onClick={mejorarPrompt} disabled={mejorando}>Generar otra</Button>
              <Button variant="contained" onClick={() => { setAgente({ ...agente, system_prompt: mejora.prompt_mejorado }); setMejOpen(false); aviso('Propuesta aplicada. Revísala y pulsa Guardar.', 'info'); }}>
                Usar esta versión
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Agente;
